import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  styled,
  Switch as MuiSwitch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import {
  Coordinates,
  DasIdWillAssign,
  InputValidationError,
} from '../../../types';
import { Device } from '../../../types/Device';

import { getProjectDasWBGT } from '../../../apis/DasWbgtApi';
import { getEndpointByDasId } from '../../../apis/devices/CommonDevice';
import { useAppSelector } from '../../../hooks';
import useDebounce from '../../../hooks/useDebounce';

import DeleteSvgIcon from '../../../assets/SvgIcon/DeleteSvgIcon';
import InfoCircleSolidSvgIcon from '../../../assets/SvgIcon/InfoCircleSolidSvgIcon';
import PinSvgIcon from '../../../assets/SvgIcon/PinSvgIcon';
import { checkDasWBGTRequiredFields } from '../../../pages/project/setting/daswbgt/daswbgt-validation';
import {
  isLatitude,
  isLongitude,
  splitCoord,
} from '../../../utils/separateLongLat';
import DialogSetPinLocation3D from '../../DialogPinLocation/DialogSetPinLocation3D';
import SelectBindToDevice from '../../SelectBindToDevice';
import InputField from '../../TextField/InputField';

/* ── Types ── */

export interface DasWBGTEndpointUpdate {
  endpointId: string;
  name: string;
  coordinates?: Coordinates;
  isDefaultWbgt: boolean;
  isDirty: boolean;
  isValid: boolean;
}

interface DasWBGTConnectedDeviceCardProps {
  projectId: string | undefined;
  devices: Device[];
  isLoading?: boolean;
  index: number;
  connectedDevice: DasIdWillAssign;
  onChange: (index: number, device: DasIdWillAssign | undefined) => void;
  onDeleteCard: (index: number) => void;
  onEndpointChange: (dasId: string, update: DasWBGTEndpointUpdate) => void;
}

/* ── Styled components ── */

const Container = styled('div')`
  width: 100%;
  background: rgba(0, 0, 0, 0.2);
  margin-bottom: 10px;
  border-radius: 4px;
`;

const Content = styled('div')`
  border-radius: 4px;
  padding: 10px;
`;

const Header = styled('div')`
  display: flex;
  align-items: center;
  height: 32px;
  color: white;
  padding-left: 10px;
`;

const ContainerDelete = styled('div')`
  height: 32px;
  width: 32px;
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.3s;
  border-radius: 5px;
  cursor: pointer;
  :hover {
    background-color: rgba(0, 0, 0, 0.2);
  }
`;

const SectionLabel = styled(Typography)`
  color: ${({ theme }) => theme.color.box_bbg};
  font-size: 12px;
  margin-bottom: 4px;
`;

const ContainerInfoIcon = styled('div')`
  height: 24px;
  width: 24px;
  display: inline-block;
`;

const ContainerSwitch = styled('div')`
  height: 40px;
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: 8px;

  & > p {
    color: white;
    font-family: 'Noto Sans';
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
  }
`;

const Switch = styled(MuiSwitch)`
  width: 36px;
  height: 20px;
  padding: 0px;

  & .MuiSwitch-switchBase {
    padding: 0px;
    transition-duration: 300ms;
    margin-left: 2px;
    &.Mui-checked {
      transform: translateX(16px) translateY(0);
      color: #fff;
      & + .MuiSwitch-track {
        background-color: ${({ theme }) => theme.color.primary.$80};
        opacity: 1;
        border: 0;
      }
      &.Mui-disabled + .MuiSwitch-track {
        opacity: 0.5;
      }
    }
    &.Mui-focusVisible .MuiSwitch-thumb {
      border: 6px solid #fff;
    }
    &.Mui-disabled .MuiSwitch-thumb {
      color: ${({ theme }) =>
        theme.palette.mode === 'light'
          ? theme.palette.grey[100]
          : theme.palette.grey[600]};
    }
    &.Mui-disabled + .MuiSwitch-track {
      opacity: ${({ theme }) => (theme.palette.mode === 'light' ? 0.7 : 0.3)};
    }
  }

  & .MuiSwitch-thumb {
    box-sizing: border-box;
    width: 12px;
    height: 12px;
    margin-top: 4px;
    color: white;
  }

  & .MuiSwitch-track {
    border-radius: 13px;
    background-color: ${({ theme }) => theme.color.secondary.$60};
    opacity: 1;
    transition: ${({ theme }) =>
      theme.transitions.create(['background-color'], { duration: 500 })};
  }
`;

const DefaultErrorRow = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 4px;
  color: ${({ theme }) => theme.color.highlight};
`;

const DefaultErrorText = styled(Typography)`
  font-size: 12px !important;
  line-height: 1.4 !important;
  color: ${({ theme }) => theme.color.highlight};
`;

/* ── Component ── */

const DasWBGTConnectedDeviceCard: React.FC<DasWBGTConnectedDeviceCardProps> = ({
  projectId,
  devices,
  isLoading = false,
  index,
  connectedDevice,
  onChange,
  onDeleteCard,
  onEndpointChange,
}) => {
  const { t } = useTranslation();
  const project = useAppSelector((store) => store.projects.currentProject);

  const dasId = connectedDevice.dasId ?? '';

  const [localName, setLocalName] = useState('');
  const [localLongLat, setLocalLongLat] = useState('');
  const [localAltitude, setLocalAltitude] = useState('');
  const [pinLocation, setPinLocation] = useState<[number, number]>([0, 0]);
  const [isDefaultWbgt, setIsDefaultWbgt] = useState(false);
  const [openPinLocation, setOpenPinLocation] = useState(false);
  const [dasIDError, setDasIDError] = useState<InputValidationError>({
    isError: false,
    message: '',
  });

  const queryLatLong = useDebounce(localLongLat, 1000);

  /* ── Fetch endpoint data for the selected device ── */
  const { data: endpointRes } = useQuery({
    queryKey: ['daswbgt-endpoint-card', projectId, dasId],
    queryFn: () =>
      getEndpointByDasId({ projectId: projectId as string, dasId }),
    enabled: !!projectId && !!dasId,
    refetchOnWindowFocus: false,
  });

  const endpoint = endpointRes?.data.data;

  /* ── Fetch all WBGTs to check if another is already default ── */
  const { data: allWBGTs } = useQuery({
    queryKey: ['project-daswbgts-default-check-card', projectId],
    queryFn: async () => {
      const res = await getProjectDasWBGT({
        projectId: projectId as string,
        params: {},
      });
      return res.data.data;
    },
    enabled: !!projectId && !!dasId,
    refetchOnWindowFocus: false,
  });

  const { data: hasOtherDefault } = useQuery({
    queryKey: [
      'daswbgt-other-default-card',
      projectId,
      dasId,
      allWBGTs?.map((d) => d.dasId).join(','),
    ],
    queryFn: async () => {
      if (!allWBGTs || !projectId) return false;
      const others = allWBGTs.filter((d) => d.dasId !== dasId);
      if (others.length === 0) return false;
      const results = await Promise.all(
        others.map((d) =>
          getEndpointByDasId({
            projectId: projectId as string,
            dasId: d.dasId,
          }),
        ),
      );
      return results.some((r) => r.data.data.isDefaultWbgt === true);
    },
    enabled: !!projectId && !!dasId && !!allWBGTs,
    refetchOnWindowFocus: false,
  });

  /* ── Seed form when endpoint ID changes (different device loaded) ── */
  useEffect(() => {
    if (!endpoint) return;
    setLocalName(endpoint.name ?? '');
    setIsDefaultWbgt(endpoint.isDefaultWbgt ?? false);
    if (endpoint.coordinates?.lon && endpoint.coordinates?.lat) {
      setPinLocation([endpoint.coordinates.lon, endpoint.coordinates.lat]);
    } else if (project) {
      setPinLocation(project.center as [number, number]);
    }
    setLocalAltitude(
      endpoint.coordinates?.alt != null ? String(endpoint.coordinates.alt) : '',
    );
  }, [endpoint?.id]);

  /* ── Fall back to project center ── */
  useEffect(() => {
    if (project && pinLocation.every((v) => v === 0)) {
      setPinLocation(project.center as [number, number]);
    }
  }, [pinLocation, project]);

  /* ── Sync typed lat/lon → pinLocation ── */
  useEffect(() => {
    if (!queryLatLong) return;
    const { lon, lat } = splitCoord(queryLatLong);
    if (isLongitude(lon) && isLatitude(lat)) {
      setPinLocation([lon, lat]);
    }
  }, [queryLatLong]);

  /* ── Sync pinLocation → input field ── */
  useEffect(() => {
    if (isLongitude(pinLocation[0]) && isLatitude(pinLocation[1])) {
      setLocalLongLat(pinLocation.join(', '));
    }
  }, [pinLocation]);

  const showDefaultError =
    isDefaultWbgt && hasOtherDefault === true && !endpoint?.isDefaultWbgt;

  /* ── Bubble endpoint changes to parent ── */
  useEffect(() => {
    if (!endpoint?.id || !dasId) return;
    const altNum = Number(localAltitude);
    const isDirty =
      localName !== (endpoint.name ?? '') ||
      isDefaultWbgt !== (endpoint.isDefaultWbgt ?? false) ||
      pinLocation[0] !== (endpoint.coordinates?.lon ?? 0) ||
      pinLocation[1] !== (endpoint.coordinates?.lat ?? 0) ||
      altNum !== (endpoint.coordinates?.alt ?? 0);
    const isValid =
      !checkDasWBGTRequiredFields(localName, localAltitude, localLongLat) &&
      !showDefaultError;
    onEndpointChange(dasId, {
      endpointId: endpoint.id,
      name: localName,
      coordinates: {
        lon: pinLocation[0],
        lat: pinLocation[1],
        alt: altNum,
      },
      isDefaultWbgt,
      isDirty,
      isValid,
    });
  }, [
    localName,
    pinLocation,
    localAltitude,
    localLongLat,
    isDefaultWbgt,
    showDefaultError,
    endpoint?.id,
    dasId,
  ]);

  const deviceList = devices.map((device) => ({
    id: device.id,
    dasId: device.dasId,
    name: device.dasId,
    batteryLevel: 0,
    bindTo: undefined,
    isAssigned: false,
    deviceType: 'daswbgt' as const,
  }));

  return (
    <Container>
      <Header>
        <Typography variant="body2">{t('connected-device')}</Typography>
        <ContainerDelete onClick={() => onDeleteCard(index)}>
          <DeleteSvgIcon />
        </ContainerDelete>
      </Header>
      <Content>
        <SectionLabel variant="body2">
          {t(
            'project-settingJson.page.equipment-management.dialog.create-equipment.connected-device.title',
          )}
        </SectionLabel>
        <SelectBindToDevice
          data={deviceList}
          isLoading={isLoading}
          placeholder={`Select DasWBGT`}
          popperPlaceholder="Das ID"
          onSelect={(d) => {
            onChange(index, {
              id: d?.id ?? '',
              dasId: d?.dasId ?? '',
              name: d?.name ?? '',
              batteryLevel: 0,
              deviceType: 'daswbgt',
            });
          }}
          onBlur={() => {
            if (!dasId) {
              setDasIDError({
                isError: true,
                message: t(
                  'project-settingJson.page.equipment-management.dialog.create-equipment.connected-device.select-device',
                ),
              });
            } else {
              setDasIDError({ isError: false, message: '' });
            }
          }}
          selectedId={connectedDevice.id}
          error={dasIDError}
          type="daswbgt"
        />

        {!!dasId && (
          <>
            <InputField
              title={t('project-settingJson.page.devices.daswbgt.name')}
              name="daswbgt-name"
              value={localName}
              isRequired
              onChange={(e) => setLocalName(e.currentTarget.value)}
              error={
                !localName
                  ? { isError: true, message: t('inputIsEmpty') }
                  : undefined
              }
            />
            <InputField
              title={t('latlong')}
              name="daswbgt-latlong"
              value={localLongLat}
              isRequired
              onChange={(e) => setLocalLongLat(e.target.value)}
              error={
                !localLongLat
                  ? { isError: true, message: t('inputIsEmpty') }
                  : undefined
              }
              endAdornment={
                <PinSvgIcon
                  sx={{
                    width: '32px',
                    height: '32px',
                    cursor: 'pointer',
                    ':hover': {
                      background: 'rgba(0,0,0,0.3)',
                      borderRadius: '4px',
                    },
                  }}
                  color="inherit"
                  onClick={() => setOpenPinLocation(true)}
                />
              }
            />
            <InputField
              title={t('altitude')}
              name="daswbgt-altitude"
              value={localAltitude}
              isRequired
              type="number"
              inputProps={{ min: 0, inputMode: 'numeric', step: '0.1' }}
              onChange={(e) => setLocalAltitude(e.currentTarget.value)}
              error={
                dasId && !localAltitude
                  ? { isError: true, message: t('inputIsEmpty') }
                  : undefined
              }
              infoIcon={
                <Tooltip
                  title={t('sea-level-info').toString()}
                  placement="top"
                  arrow
                >
                  <ContainerInfoIcon>
                    <InfoCircleSolidSvgIcon />
                  </ContainerInfoIcon>
                </Tooltip>
              }
            />

            <ContainerSwitch>
              <Switch
                checked={isDefaultWbgt}
                onChange={(e) => setIsDefaultWbgt(e.target.checked)}
              />
              <Typography variant="body2">
                {t('project-settingJson.page.devices.daswbgt.default-wbgt')}
              </Typography>
            </ContainerSwitch>
            {showDefaultError && (
              <DefaultErrorRow>
                <InfoCircleSolidSvgIcon
                  sx={{
                    width: '16px',
                    height: '16px',
                    flexShrink: 0,
                    mt: '1px',
                  }}
                />
                <DefaultErrorText>
                  {t(
                    'project-settingJson.page.devices.daswbgt.default-wbgt-error',
                  )}
                </DefaultErrorText>
              </DefaultErrorRow>
            )}
          </>
        )}

        {openPinLocation && (
          <DialogSetPinLocation3D
            open={openPinLocation}
            onClose={() => setOpenPinLocation(false)}
            mode="beacon"
            pinLocation={pinLocation}
            setPinLocation={setPinLocation}
            altitude={Number(localAltitude)}
            setAltitude={(v) => setLocalAltitude(String(v))}
          />
        )}
      </Content>
    </Container>
  );
};

export default DasWBGTConnectedDeviceCard;
